import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/ui/PageHeader';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { StatusBadge } from '@/ui/StatusBadge';
import { Button } from '@/ui/base/button';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/base/tooltip';
import { Plus, Pencil, Trash2, Lock, Eye } from 'lucide-react';
import {
  usePermissionProfiles,
  useDeletePermissionProfile,
  useSystemRoleUserCounts,
} from '@/lib/usePermissionProfiles';
import { SYSTEM_ROLE_LABELS } from '@/lib/permissionModules';
import type { AppRole } from '@/lib/useUsers';
import { toast } from 'sonner';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';

const SYSTEM_ROLES: AppRole[] = [
  'ADMIN',
  'GESTOR_ADMIN',
  'FINANCEIRO',
  'GESTOR',
  'FISCAL',
  'CREDENCIADO',
  'AUDITOR',
];

export default function PerfisList() {
  const navigate = useNavigate();
  const { data: profiles, isLoading } = usePermissionProfiles();
  const { data: systemCounts } = useSystemRoleUserCounts();
  const deleteMutation = useDeletePermissionProfile();

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Perfil excluído com sucesso');
    } catch {
      toast.error('Erro ao excluir perfil');
    }
  };

  const systemRows = SYSTEM_ROLES.map((role) => ({
    id: `system-${role}`,
    role,
    nome: SYSTEM_ROLE_LABELS[role].nome,
    descricao: SYSTEM_ROLE_LABELS[role].descricao,
    ativo: true,
    isSystem: true,
    user_count: systemCounts?.[role] ?? 0,
  }));

  const customRows = (profiles || []).map((p) => ({
    id: p.id,
    role: null as AppRole | null,
    nome: p.nome,
    descricao: p.descricao || '',
    ativo: p.ativo,
    isSystem: false,
    user_count: p.user_count ?? 0,
  }));

  const allRowsRaw = [...systemRows, ...customRows];

  const [filters, setFilters] = useState<Record<string, string>>({ q: '', tipo: '', status: '' });

  const allRows = useMemo(() => {
    const q = (filters.q ?? '').trim().toLowerCase();
    return allRowsRaw.filter((r) => {
      if (q && !`${r.nome} ${r.descricao}`.toLowerCase().includes(q)) return false;
      if (filters.tipo === 'sistema' && !r.isSystem) return false;
      if (filters.tipo === 'custom' && r.isSystem) return false;
      if (filters.status === 'ativo' && !r.ativo) return false;
      if (filters.status === 'inativo' && r.ativo) return false;
      return true;
    });
  }, [allRowsRaw, filters]);

  const filterFields: FilterFieldConfig[] = [
    { key: 'q', label: 'Buscar', type: 'search', placeholder: 'Nome ou descrição...' },
    {
      key: 'tipo',
      label: 'Tipo',
      type: 'select',
      options: [
        { value: 'sistema', label: 'Sistema' },
        { value: 'custom', label: 'Customizado' },
      ],
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'ativo', label: 'Ativo' },
        { value: 'inativo', label: 'Inativo' },
      ],
    },
  ];

  const columns: TableViewColumn<(typeof allRows)[0]>[] = [
    {
      id: 'nome',
      header: 'Nome',
      sortKey: 'nome' as const,
      cell: (row: (typeof allRows)[0]) => (
        <div className="flex items-center gap-2">
          {row.isSystem && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className="font-medium">{row.nome}</span>
          {row.isSystem && (
            <span className="text-[10px] uppercase tracking-wider bg-muted text-muted-foreground px-1.5 py-0.5 rounded-md font-semibold">
              Sistema
            </span>
          )}
        </div>
      ),
      isCritical: true,
      canHide: false,
    },
    {
      id: 'descricao',
      header: 'Descrição',
      sortKey: 'descricao' as const,
      cell: (row: (typeof allRows)[0]) => (
        <span className="text-muted-foreground text-sm">{row.descricao || '—'}</span>
      ),
    },
    {
      id: 'usuarios',
      header: 'Usuários',
      sortKey: 'user_count' as const,
      cell: (row: (typeof allRows)[0]) => (
        <span className="text-sm text-muted-foreground">{row.user_count}</span>
      ),
    },
    {
      id: 'acoes',
      header: 'Ações',
      cell: (row: (typeof allRows)[0]) => {
        if (row.isSystem) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/admin/perfis/sistema/${row.role}`)}
                  aria-label="Visualizar"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Visualizar permissões</TooltipContent>
            </Tooltip>
          );
        }

        const hasUsers = (row.user_count ?? 0) > 0;

        return (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/admin/perfis/${row.id}`)}
              aria-label="Editar"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            {hasUsers ? (
              <ConfirmDialog
                trigger={
                  <Button variant="ghost" size="icon" aria-label="Excluir">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                }
                title="Excluir perfil com usuários vinculados"
                description={`Este perfil possui ${row.user_count} usuário(s) vinculado(s). Ao excluí-lo, esses usuários perderão estas permissões. Deseja continuar?`}
                onConfirm={() => handleDelete(row.id)}
                variant="destructive"
              />
            ) : (
              <ConfirmDialog
                trigger={
                  <Button variant="ghost" size="icon" aria-label="Excluir">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                }
                title="Excluir perfil"
                description="Tem certeza que deseja excluir este perfil?"
                onConfirm={() => handleDelete(row.id)}
                variant="destructive"
              />
            )}
          </div>
        );
      },
      isActionColumn: true,
      canHide: false,
      canReorder: false,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row: (typeof allRows)[0]) => <StatusBadge status={row.ativo ? 'ativo' : 'inativo'} />,
      sortKey: (row: (typeof allRows)[0]) => (row.ativo ? 1 : 0),
      isCritical: true,
      canHide: false,
      isStatusColumn: true,
    },
  ];

  const tableView = useTableViewPreferences({
    contextKey: 'admin:perfis:list',
    columns,
  });

  return (
    <>
      <PageHeader title="Perfis de Acesso" description="Gerencie os perfis de permissão do sistema">
        <Button onClick={() => navigate('/admin/perfis/novo')}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Perfil
        </Button>
      </PageHeader>

      <FilterBar
        fields={filterFields}
        values={filters}
        onChange={(k, v) => setFilters((p) => ({ ...p, [k]: v }))}
        onClear={() => setFilters({ q: '', tipo: '', status: '' })}
      />

      <DataTable
        columns={tableView.orderedColumns as DataTableColumn<(typeof allRows)[0]>[]}
        personalizacao={tableView}
        data={allRows}
        isLoading={isLoading}
        emptyTitle="Nenhum perfil customizado"
        emptyDescription="Crie um novo perfil de acesso para definir permissões granulares."
      />
    </>
  );
}
